import React, { useEffect } from 'react';
import { Card, Typography, Row, Col, Spin, message } from 'antd';
import { usePolicySummary, usePolicyInterval } from '../utils/hooks';
import { SummaryCard } from './summaryCard';
import { SummaryTable } from './summaryTable';
import DatePickerWithMenu from '../components/datepicker';
import KebabMenu from '../components/kebab';
import { Footer } from '@src/components/common';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getUserProfile } from '@src/store/common/selectors';
import moment from 'moment';
import { DATE_SHORTCUTS } from '@utils/constants';
import { checkDate } from '../utils/helper';

const { Title } = Typography;

const PolicyTrackingCard = () => {
  const {
    interval,
    comparisonInterval,
    localInterval,
    validateUpdateInterval,
    summaryDatePickerRef,
    comparisonDatePickerRef,
  } = usePolicyInterval();

  const [searchParams, setSearchParams] = useSearchParams();
  const start_day = searchParams.get('start_day');
  const end_day = searchParams.get('end_day');
  const userProfile = useSelector(getUserProfile);
  const [isInitialized, setIsInitialized] = React.useState(false);

  useEffect(() => {
    if (!checkDate(start_day, end_day)) return;
    if (isInitialized) return;

    const interval: [moment.Moment, moment.Moment] =
      start_day && end_day
        ? [moment(start_day), moment(end_day)]
        : [localInterval[0], localInterval[1]];

    const shortcut = start_day && end_day ? 'custom' : DATE_SHORTCUTS.today;
    validateUpdateInterval(interval, shortcut);
    setIsInitialized(true);
  }, [start_day, end_day, validateUpdateInterval, isInitialized, localInterval]);

  const handleDateChange = (dates: [moment.Moment, moment.Moment], short) => {
    if (!dates || dates.length < 2) return;

    if (dates[0].isAfter(dates[1])) {
      message.error('Start date must be earlier than or equal to end date');
      return;
    }

    searchParams.set('start_day', dates[0].format('YYYY-MM-DDTHH:mm:ss'));
    searchParams.set('end_day', dates[1].format('YYYY-MM-DDTHH:mm:ss'));
    setSearchParams(searchParams);
    validateUpdateInterval(dates, short);
  };

  const {
    data: summaryData,
    isLoading: isLoading,
    isError: isError,
  } = usePolicySummary({
    start_day: start_day ?? localInterval[0].format('YYYY-MM-DD'),
    end_day: end_day ?? localInterval[1].format('YYYY-MM-DD'),
  });

  if (isError) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p style={{ color: 'red' }}>Failed to load data. Please try again later.</p>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Card
        style={{ padding: 12 }}
        title={<Title level={4}>Policy Tracking</Title>}
        extra={
          <Row align="middle" gutter={16}>
            {isInitialized ? (
              <DatePickerWithMenu
                interval={interval}
                comparisonInterval={comparisonInterval}
                validateUpdateInterval={handleDateChange}
                summaryDatePickerRef={summaryDatePickerRef}
                comparisonDatePickerRef={comparisonDatePickerRef}
              />
            ) : (
              <Spin />
            )}
            <Col>
              {summaryData ? (
                <KebabMenu detailData={summaryData} policyId={null} profile={userProfile} />
              ) : (
                <Spin />
              )}
            </Col>
          </Row>
        }
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Spin />
          </div>
        ) : (
          <>
            <SummaryCard summaryData={summaryData?.Resource ?? []} />
            <div style={{ marginBottom: '100px' }}></div>
            <SummaryTable policyList={summaryData?.Policy ?? []} />
          </>
        )}
        <Footer />
      </Card>
    </div>
  );
};

export default PolicyTrackingCard;
